import { useEffect, useState } from "react";
import { X, Download, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { makeRankCardBlob, fmt } from "@/lib/rankCard";

export default function RankCardModal({ entry, gameName, onClose }) {
  const [url, setUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setBusy(true);
      try {
        const b = await makeRankCardBlob(entry, gameName);
        if (!live) return;
        setBlob(b);
        setUrl(URL.createObjectURL(b));
      } catch (e) { toast.error("Could not generate card"); }
      finally { if (live) setBusy(false); }
    })();
    return () => { live = false; };
  }, [entry, gameName]);

  const caption = `🏆 ${entry.team_name}${entry.org_name ? ` (${entry.org_name})` : ""} escaped ${gameName || "the room"} in ${fmt(entry.completion_seconds)} at ${entry.location_name}! Beat us on Escape Room Express 👉 ${window.location.origin}/leaderboard`;

  const shareImage = async () => {
    if (!blob) return;
    const file = new File([blob], "escaperoom-rank.png", { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], text: caption }); return; }
      catch { /* user cancelled */ return; }
    }
    // desktop fallback: download image + open WhatsApp text
    download();
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank");
    toast.info("Card downloaded — attach it in WhatsApp");
  };

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = `escaperoom-rank-${entry.team_name}.png`; a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm grid place-items-center p-5" onClick={onClose} data-testid="rankcard-modal">
      <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white"><X /></button>
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#131a29] min-h-[300px] grid place-items-center">
          {busy ? <Loader2 className="h-8 w-8 animate-spin text-violet-400" /> : url && <img src={url} alt="Rank card" className="w-full" data-testid="rankcard-image" />}
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={shareImage} disabled={busy} data-testid="rankcard-share"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold transition-all disabled:opacity-50">
            <Share2 className="h-4 w-4" /> Share on WhatsApp
          </button>
          <button onClick={download} disabled={busy} data-testid="rankcard-download"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 font-semibold transition-all disabled:opacity-50">
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
